import React, { useMemo, useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Checkbox,
  FormControlLabel,
  TextField,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";

// Pure UI: callbacks do all the real work
export default function DangerZone({
  students = [],
  adults = [],
  loading = false,
  loadingAdults = false,
  deletingStudents = false,
  deletingAdults = false,
  deletingGroup = false,
  onDeleteStudents,
  onDeleteAdults,
  onDeleteGroupAndStudents,
}) {
  const [selectedStudentIds, setSelectedStudentIds] = useState([]);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmSelectedText, setConfirmSelectedText] = useState("");
  const [confirmAllInlineText, setConfirmAllInlineText] = useState("");
  const REQUIRED_PHRASE = "delete these students";

  const [selectedAdultIds, setSelectedAdultIds] = useState([]);
  const [confirmAdultsOpen, setConfirmAdultsOpen] = useState(false);
  const [confirmAdultsText, setConfirmAdultsText] = useState("");
  const REQUIRED_ADULTS_PHRASE = "delete these adults";

  const [confirmAllOpen, setConfirmAllOpen] = useState(false);
  const [confirmAllDialogText, setConfirmAllDialogText] = useState("");

  const REQUIRED_ALL_INLINE = "delete them all";
  const REQUIRED_ALL_DIALOG = "really delete everything";

  useEffect(() => {
  const validIds = new Set(students.map((s) => s.id));
  setSelectedStudentIds((prev) => prev.filter((id) => validIds.has(id)));
}, [students]);

  useEffect(() => {
    const validIds = new Set(adults.map((a) => a.id));
    setSelectedAdultIds((prev) => prev.filter((id) => validIds.has(id)));
  }, [adults]);

  const allSelected = useMemo(
    () => students.length > 0 && selectedStudentIds.length === students.length,
    [students, selectedStudentIds]
  );

  const allAdultsSelected = useMemo(
    () => adults.length > 0 && selectedAdultIds.length === adults.length,
    [adults, selectedAdultIds]
  );

  const isDeleteStudentsDisabled =
    deletingStudents || !onDeleteStudents || selectedStudentIds.length === 0;

  const isDeleteAdultsDisabled =
    deletingAdults || !onDeleteAdults || selectedAdultIds.length === 0;

  const isDeleteGroupDisabled =
    deletingGroup ||
    !onDeleteGroupAndStudents ||
    confirmAllInlineText.trim().toLowerCase() !== REQUIRED_ALL_INLINE;

  const toggleStudent = (id) => {
    setSelectedStudentIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const toggleAll = () => {
    if (allSelected) {
      setSelectedStudentIds([]);
    } else {
      setSelectedStudentIds(students.map((s) => s.id));
    }
  };

  const toggleAdult = (id) => {
    setSelectedAdultIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const toggleAllAdults = () => {
    if (allAdultsSelected) {
      setSelectedAdultIds([]);
    } else {
      setSelectedAdultIds(adults.map((a) => a.id));
    }
  };

  const handleDeleteSelectedStudents = () => {
    if (!onDeleteStudents || selectedStudentIds.length === 0) return;
    setConfirmSelectedText("");
    setConfirmOpen(true);
  };

    const canConfirm =
    confirmSelectedText.trim().toLowerCase() === REQUIRED_PHRASE &&
    !deletingStudents &&
    !!onDeleteStudents;

  const confirmDeleteSelected = () => {
    if (!canConfirm) return;
    onDeleteStudents(selectedStudentIds);
    setConfirmOpen(false);
  };

  const selectedNames = useMemo(() => {
    const map = new Map(students.map((s) => [s.id, s.name]));
    return selectedStudentIds.map((id) => map.get(id) || `Student ${id}`);
  }, [students, selectedStudentIds]);

  const handleDeleteSelectedAdults = () => {
    if (!onDeleteAdults || selectedAdultIds.length === 0) return;
    setConfirmAdultsText("");
    setConfirmAdultsOpen(true);
  };

  const canConfirmAdults =
    confirmAdultsText.trim().toLowerCase() === REQUIRED_ADULTS_PHRASE &&
    !deletingAdults &&
    !!onDeleteAdults;

  const confirmDeleteSelectedAdults = () => {
    if (!canConfirmAdults) return;
    onDeleteAdults(selectedAdultIds);
    setConfirmAdultsOpen(false);
  };

  const selectedAdultNames = useMemo(() => {
    const map = new Map(adults.map((a) => [a.id, a.name]));
    return selectedAdultIds.map((id) => map.get(id) || `Adult ${id}`);
  }, [adults, selectedAdultIds]);

  const handleDeleteGroupAndStudentsClick = () => {
  setConfirmAllDialogText("");
  setConfirmAllOpen(true);
};


  const canConfirmAllDialog =
  confirmAllDialogText.trim().toLowerCase() === REQUIRED_ALL_DIALOG &&
  !deletingGroup &&
  !!onDeleteGroupAndStudents;

const confirmDeleteAllAndGroup = () => {
  if (!canConfirmAllDialog) return;
  onDeleteGroupAndStudents();
  setConfirmAllOpen(false);
};


  return (
    <Box
      sx={{
        border: "1px solid #f44336",
        borderRadius: 1,
        p: 2,
        bgcolor: "#ffebee",
        display: "flex",
        flexDirection: "column",
        gap: 2,
      }}
    >
      <Typography variant="h6" sx={{ color: "#b71c1c" }}>
        Danger Zone
      </Typography>

      {/* SECTION 1 – Permanently delete selected students */}
      <Box
        sx={{
          borderRadius: 1,
          p: 2,
          bgcolor: "white",
          border: "1px solid #ffcdd2",
        }}
      >
        <Typography variant="subtitle1" sx={{ mb: 1, color: "#b71c1c" }}>
          Permanently delete students from this group
        </Typography>
        <Typography variant="body2" sx={{ mb: 2 }}>
          This will permanently delete the selected students from the entire
          system (not just remove them from this group). This action cannot be
          undone.
        </Typography>

        {loading ? (
          <Typography variant="body2">
            <em>Loading students…</em>
          </Typography>
        ) : students.length === 0 ? (
          <Typography variant="body2" sx={{ fontStyle: "italic" }}>
            There are no students in this group.
          </Typography>
        ) : (
          <>
            <FormControlLabel
              control={
                <Checkbox
                  checked={allSelected}
                  indeterminate={
                    selectedStudentIds.length > 0 && !allSelected
                  }
                  onChange={toggleAll}
                  disabled={deletingStudents || deletingGroup}
                />
              }
              label="Select all students"
              sx={{ mb: 1 }}
            />

            <Box
              sx={{
                maxHeight: 200,
                overflowY: "auto",
                borderRadius: 1,
                border: "1px solid #ffcdd2",
                p: 1,
                mb: 2,
                bgcolor: "#fffafa",
              }}
            >
              {students.map((s) => (
                <FormControlLabel
                  key={s.id}
                  control={
                    <Checkbox
                      checked={selectedStudentIds.includes(s.id)}
                      onChange={() => toggleStudent(s.id)}
                      disabled={deletingStudents || deletingGroup}
                    />
                  }
                  label={s.name || `Student ${s.id}`}
                  sx={{ display: "block", m: 0 }}
                />
              ))}
            </Box>

            <Button
              variant="contained"
              color="error"
              startIcon={<DeleteIcon />}
              disabled={isDeleteStudentsDisabled}
              onClick={handleDeleteSelectedStudents}
            >
              {deletingStudents
                ? "Deleting selected students…"
                : "Delete selected students"}
            </Button>
          </>
        )}
      </Box>

      <Divider />

      {/* SECTION 2 – Permanently delete selected adults */}
      <Box
        sx={{
          borderRadius: 1,
          p: 2,
          bgcolor: "white",
          border: "1px solid #ffcdd2",
        }}
      >
        <Typography variant="subtitle1" sx={{ mb: 1, color: "#b71c1c" }}>
          Permanently delete adults from this group
        </Typography>
        <Typography variant="body2" sx={{ mb: 2 }}>
          This will permanently delete the selected adults from the entire
          system (not just remove them from this group). This action cannot be
          undone.
        </Typography>

        {loadingAdults ? (
          <Typography variant="body2">
            <em>Loading adults…</em>
          </Typography>
        ) : adults.length === 0 ? (
          <Typography variant="body2" sx={{ fontStyle: "italic" }}>
            There are no adults in this group.
          </Typography>
        ) : (
          <>
            <FormControlLabel
              control={
                <Checkbox
                  checked={allAdultsSelected}
                  indeterminate={
                    selectedAdultIds.length > 0 && !allAdultsSelected
                  }
                  onChange={toggleAllAdults}
                  disabled={deletingAdults || deletingGroup}
                />
              }
              label="Select all adults"
              sx={{ mb: 1 }}
            />

            <Box
              sx={{
                maxHeight: 200,
                overflowY: "auto",
                borderRadius: 1,
                border: "1px solid #ffcdd2",
                p: 1,
                mb: 2,
                bgcolor: "#fffafa",
              }}
            >
              {adults.map((a) => (
                <FormControlLabel
                  key={a.id}
                  control={
                    <Checkbox
                      checked={selectedAdultIds.includes(a.id)}
                      onChange={() => toggleAdult(a.id)}
                      disabled={deletingAdults || deletingGroup}
                    />
                  }
                  label={a.name || `Adult ${a.id}`}
                  sx={{ display: "block", m: 0 }}
                />
              ))}
            </Box>

            <Button
              variant="contained"
              color="error"
              startIcon={<DeleteIcon />}
              disabled={isDeleteAdultsDisabled}
              onClick={handleDeleteSelectedAdults}
            >
              {deletingAdults
                ? "Deleting selected adults…"
                : "Delete selected adults"}
            </Button>
          </>
        )}
      </Box>

      <Divider />

      {/* SECTION 3 – Delete group + ALL its students */}
      <Box
        sx={{
          borderRadius: 1,
          p: 2,
          bgcolor: "white",
          border: "1px solid #ffcdd2",
        }}
      >
        <Typography variant="subtitle1" sx={{ mb: 1, color: "#b71c1c" }}>
          Delete this group and all its members
        </Typography>
        <Typography variant="body2" sx={{ mb: 2 }}>
          This will permanently delete the group and{" "}
          <strong>all students and adults currently in this group</strong> from
          the entire system.
          <br />
          This action cannot be undone.
        </Typography>

        <Typography variant="body2" sx={{ mb: 1 }}>
          To enable this function, type{" "}
          <strong>{REQUIRED_ALL_INLINE}</strong> in the box below:
        </Typography>

        <TextField
          fullWidth
          size="small"
          value={confirmAllInlineText}
          onChange={(e) => setConfirmAllInlineText(e.target.value)}
          placeholder={`Type "${REQUIRED_ALL_INLINE}" to enable the button`}
          disabled={deletingGroup}
          sx={{ mb: 2, bgcolor: "#fffafa" }}
        />

        <Button
          variant="contained"
          color="error"
          startIcon={<DeleteIcon />}
          disabled={isDeleteGroupDisabled}
          onClick={handleDeleteGroupAndStudentsClick}
        >
          {deletingGroup
            ? "Deleting group and members…"
            : "Delete group and ALL members"}
        </Button>
      </Box>

      {/* CONFIRMATION DIALOG for selected students */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Confirm permanent deletion</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            You are about to permanently delete{" "}
            <strong>{selectedStudentIds.length}</strong> student(s) from the system:
          </Typography>

          <Box component="ul" sx={{ mt: 0, mb: 2, pl: 3 }}>
            {selectedNames.slice(0, 8).map((n) => (
              <li key={n}>
                <Typography variant="body2">{n}</Typography>
              </li>
            ))}
            {selectedNames.length > 8 && (
              <li>
                <Typography variant="body2">
                  …and {selectedNames.length - 8} more
                </Typography>
              </li>
            )}
          </Box>

          <Typography variant="body2" sx={{ mb: 1 }}>
            To confirm, type <strong>{REQUIRED_PHRASE}</strong>:
          </Typography>

          <TextField
            fullWidth
            size="small"
            value={confirmSelectedText}
            onChange={(e) => setConfirmSelectedText(e.target.value)}
            placeholder={`Type "${REQUIRED_PHRASE}"`}
            autoFocus
            disabled={deletingStudents}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} disabled={deletingStudents}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={confirmDeleteSelected}
            disabled={!canConfirm}
          >
            {deletingStudents ? "Deleting…" : "Yes, delete them"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* CONFIRMATION DIALOG for selected adults */}
      <Dialog open={confirmAdultsOpen} onClose={() => setConfirmAdultsOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Confirm permanent deletion</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            You are about to permanently delete{" "}
            <strong>{selectedAdultIds.length}</strong> adult(s) from the system:
          </Typography>

          <Box component="ul" sx={{ mt: 0, mb: 2, pl: 3 }}>
            {selectedAdultNames.slice(0, 8).map((n) => (
              <li key={n}>
                <Typography variant="body2">{n}</Typography>
              </li>
            ))}
            {selectedAdultNames.length > 8 && (
              <li>
                <Typography variant="body2">
                  …and {selectedAdultNames.length - 8} more
                </Typography>
              </li>
            )}
          </Box>

          <Typography variant="body2" sx={{ mb: 1 }}>
            To confirm, type <strong>{REQUIRED_ADULTS_PHRASE}</strong>:
          </Typography>

          <TextField
            fullWidth
            size="small"
            value={confirmAdultsText}
            onChange={(e) => setConfirmAdultsText(e.target.value)}
            placeholder={`Type "${REQUIRED_ADULTS_PHRASE}"`}
            autoFocus
            disabled={deletingAdults}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmAdultsOpen(false)} disabled={deletingAdults}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={confirmDeleteSelectedAdults}
            disabled={!canConfirmAdults}
          >
            {deletingAdults ? "Deleting…" : "Yes, delete them"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* CONFIRMATION DIALOG for ALL students */}
      <Dialog open={confirmAllOpen} onClose={() => setConfirmAllOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Final confirmation</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            This will permanently delete the group and ALL students and adults
            currently in it. This cannot be undone.
          </Typography>

          <Typography variant="body2" sx={{ mb: 1 }}>
            To proceed, type <strong>{REQUIRED_ALL_DIALOG}</strong>:
          </Typography>

          <TextField
            fullWidth
            size="small"
            value={confirmAllDialogText}
            onChange={(e) => setConfirmAllDialogText(e.target.value)}
            placeholder={`Type "${REQUIRED_ALL_DIALOG}"`}
            autoFocus
            disabled={deletingGroup}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmAllOpen(false)} disabled={deletingGroup}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={confirmDeleteAllAndGroup}
            disabled={!canConfirmAllDialog}
          >
            {deletingGroup ? "Deleting…" : "Yes, delete everything"}
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
}
